/**
 * The Level Creator's recolouring (spec 2026-11-01). Pure — pixels in, pixels
 * out — so it is unit-tested without a canvas.
 *
 * A sprite's opaque pixels are sorted into COLOUR GROUPS: saturated pixels by
 * hue (twelve 30° buckets), greys by lightness (dark / mid / light). The
 * biggest groups (up to six) are what the colour menu shows, one square each,
 * named by the group's average colour. Repainting a group gives every pixel
 * in it the new hue and saturation and scales its lightness by how light that
 * pixel was against the group's average — so shading and outlines survive and
 * the piece still looks drawn.
 */

type RGB = [number, number, number];

function rgbToHsl(r: number, g: number, b: number): RGB {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h * 60, s, l];
}

function hslToRgb(h: number, s: number, l: number): RGB {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = h / 360;
  const f = (t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [
    Math.round(f(hk + 1 / 3) * 255),
    Math.round(f(hk) * 255),
    Math.round(f(hk - 1 / 3) * 255),
  ];
}

const hex = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

function parseHex(h: string): RGB {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

/** A pixel's bucket: 0–11 by hue when saturated, 12–14 greys by lightness; -1 transparent. */
function bucketOf(r: number, g: number, b: number, a: number): number {
  if (a < 32) return -1;
  const [h, s, l] = rgbToHsl(r, g, b);
  if (s < 0.18 || l < 0.08 || l > 0.94) return 12 + Math.min(2, Math.floor(l * 3));
  return Math.floor(h / 30) % 12;
}

interface Group {
  bucket: number;
  count: number;
  sum: RGB;
}

function measure(
  pixels: Uint8ClampedArray,
  max: number,
): { groups: Group[]; hexOf: Map<number, string> } {
  const byBucket = new Map<number, Group>();
  for (let i = 0; i < pixels.length; i += 4) {
    const k = bucketOf(pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]);
    if (k < 0) continue;
    let g = byBucket.get(k);
    if (!g) {
      g = { bucket: k, count: 0, sum: [0, 0, 0] };
      byBucket.set(k, g);
    }
    g.count++;
    g.sum[0] += pixels[i];
    g.sum[1] += pixels[i + 1];
    g.sum[2] += pixels[i + 2];
  }
  const total = [...byBucket.values()].reduce((n, g) => n + g.count, 0);
  const groups = [...byBucket.values()]
    // A sliver under 0.5% of the sprite is anti-aliasing, not a colour.
    .filter((g) => g.count >= Math.max(1, total * 0.005))
    .sort((a, b) => b.count - a.count || a.bucket - b.bucket)
    .slice(0, max);
  const hexOf = new Map<number, string>();
  for (const g of groups)
    hexOf.set(g.bucket, hex(g.sum[0] / g.count, g.sum[1] / g.count, g.sum[2] / g.count));
  return { groups, hexOf };
}

/** The colour groups of a sprite, biggest first, as `#rrggbb`. */
export function colorGroups(pixels: Uint8ClampedArray, max = 6): string[] {
  const { groups, hexOf } = measure(pixels, max);
  return groups.map((g) => hexOf.get(g.bucket)!);
}

/** A copy of `pixels` with each group in `map` (group hex → new hex) repainted. */
export function recolorPixels(
  pixels: Uint8ClampedArray,
  map: Readonly<Record<string, string>>,
  max = 6,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(pixels);
  if (Object.keys(map).length === 0) return out;
  const { hexOf } = measure(pixels, max);
  const plan = new Map<number, { target: RGB; groupL: number }>();
  for (const [bucket, h] of hexOf) {
    const to = map[h];
    if (!to) continue;
    const [r, g, b] = parseHex(h);
    plan.set(bucket, {
      target: rgbToHsl(...parseHex(to)),
      groupL: Math.max(0.02, rgbToHsl(r, g, b)[2]),
    });
  }
  if (plan.size === 0) return out;
  for (let i = 0; i < pixels.length; i += 4) {
    const k = bucketOf(pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]);
    const p = plan.get(k);
    if (!p) continue;
    const l = rgbToHsl(pixels[i], pixels[i + 1], pixels[i + 2])[2];
    const [th, ts, tl] = p.target;
    const nl = Math.max(0, Math.min(1, (tl * l) / p.groupL));
    const [r, g, b] = hslToRgb(th, ts, nl);
    out[i] = r;
    out[i + 1] = g;
    out[i + 2] = b;
  }
  return out;
}

/** A short stable hash of a colour map, for texture keys. */
export function colorsHash(map: Readonly<Record<string, string>>): string {
  const s = Object.keys(map)
    .sort()
    .map((k) => `${k}${map[k]}`)
    .join('');
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
}

// ── The palette (shaped like the reference picker) ───────────────────────────

const BRIGHTS: RGB[] = [
  [152, 0, 0],
  [255, 0, 0],
  [255, 153, 0],
  [255, 255, 0],
  [0, 255, 0],
  [0, 255, 255],
  [74, 134, 232],
  [0, 0, 255],
  [153, 0, 255],
  [255, 0, 255],
];

const mix = (c: RGB, to: number, t: number): RGB => [
  c[0] + (to - c[0]) * t,
  c[1] + (to - c[1]) * t,
  c[2] + (to - c[2]) * t,
];

/** 80 swatches: a grey ramp, ten brights, then three tints and three shades of each. */
export const PALETTE: readonly string[] = [
  ...Array.from({ length: 10 }, (_, i) => {
    const v = Math.round((i / 9) * 255);
    return hex(v, v, v);
  }),
  ...BRIGHTS.map((c) => hex(...c)),
  ...[0.8, 0.6, 0.4].flatMap((t) => BRIGHTS.map((c) => hex(...mix(c, 255, t)))),
  ...[0.25, 0.5, 0.7].flatMap((t) => BRIGHTS.map((c) => hex(...mix(c, 0, t)))),
];
